import {
  DispatchableType,
  EndCustomer,
  HoldStatus,
  RecurrenceType,
  ShipmentStatus,
} from "@api/graphql/generated/generated-types";
import { CustomFilter, ShipmentsPrimaryFilterType } from "../types";
import {
  currentEpochSeconds,
  minutesAfterCurrentEpochSeconds,
} from "@src/common/lib/DateUtils";
import { datePickerDateToISODate } from "@src/common/utils/timezoneConverters";

export function makeGetShipmentsVariable(
  courierId: string,
  selectedPrimaryFilter: ShipmentsPrimaryFilterType,
  minDate: Date | undefined,
  maxDate: Date | undefined,
  driverIdsFilter: string[] | undefined,
  selectedCustomer: EndCustomer | undefined,
  selectedCustomFilter: CustomFilter | undefined,
  shipmentDateRangeStart: Date | undefined,
  shipmentDateRangeEnd: Date | undefined,
  completedDateRangeStart: Date | undefined,
  completedDateRangeEnd: Date | undefined
) {
  let lateByFilter: number | undefined = undefined;
  let minClosingTimeFilter: number | undefined = undefined;
  let likelyLateOnlyFilter: boolean | undefined = undefined;
  let isClosedFilter: boolean | undefined = undefined;
  let isAssignedFilter: boolean | undefined = undefined;
  let holdStatusFilter: HoldStatus | undefined = undefined;
  let statusListFilter: ShipmentStatus[] | undefined = [
    ShipmentStatus.Created,
    ShipmentStatus.Assigned,
    ShipmentStatus.Routed,
    ShipmentStatus.Paused,
    ShipmentStatus.InTransit,
  ];
  let serviceTypeIds: string[] | undefined = undefined;
  let endCustomerIdsFilter: string[] | undefined = selectedCustomer
    ? [selectedCustomer.id]
    : undefined;
  let excludeWordsFromNoteFilter: string[] | undefined = undefined;
  let isConfirmed: boolean | undefined = undefined;
  let isAttempted: boolean | undefined = undefined;
  let recurrenceTypes: RecurrenceType[] | undefined = undefined;
  // A non-dispatchable order is a billing record — the driving happens on its
  // legs — so it is not work and does not belong in the tabs a dispatcher uses
  // to find work. It stays visible everywhere else, including Active, because
  // it is still the customer's order and carries the price and the invoice.
  let dispatchableTypes: DispatchableType[] | undefined = undefined;
  const currentEpoch = currentEpochSeconds();
  switch (selectedPrimaryFilter) {
    case ShipmentsPrimaryFilterType.ALL:
      statusListFilter = undefined;
      isClosedFilter = undefined;
      break;
    case ShipmentsPrimaryFilterType.ACTIVE:
      isClosedFilter = false;
      holdStatusFilter = HoldStatus.None;
      break;
    case ShipmentsPrimaryFilterType.UNASSIGNED:
      isAssignedFilter = false;
      holdStatusFilter = HoldStatus.None;
      dispatchableTypes = [DispatchableType.Dispatchable];
      break;
    case ShipmentsPrimaryFilterType.ASSIGNED:
      isAssignedFilter = true;
      holdStatusFilter = HoldStatus.None;
      break;
    case ShipmentsPrimaryFilterType.ON_HOLD:
      holdStatusFilter = HoldStatus.OnHold;
      break;
    case ShipmentsPrimaryFilterType.LATE:
      lateByFilter = currentEpoch;
      isClosedFilter = false;
      holdStatusFilter = HoldStatus.None;
      dispatchableTypes = [DispatchableType.Dispatchable];
      break;
    case ShipmentsPrimaryFilterType.LATE_IN_15_MINUTES:
      lateByFilter = minutesAfterCurrentEpochSeconds(15);
      minClosingTimeFilter = currentEpoch;
      isClosedFilter = false;
      holdStatusFilter = HoldStatus.None;
      break;
    case ShipmentsPrimaryFilterType.LATE_IN_30_MINUTES:
      lateByFilter = minutesAfterCurrentEpochSeconds(30);
      minClosingTimeFilter = currentEpoch;
      isClosedFilter = false;
      holdStatusFilter = HoldStatus.None;
      break;
    case ShipmentsPrimaryFilterType.LATE_IN_60_MINUTES:
      lateByFilter = minutesAfterCurrentEpochSeconds(60);
      minClosingTimeFilter = currentEpoch;
      isClosedFilter = false;
      holdStatusFilter = HoldStatus.None;
      break;
    case ShipmentsPrimaryFilterType.LIKELY_LATE:
      likelyLateOnlyFilter = true;
      minClosingTimeFilter = currentEpoch;
      isClosedFilter = false;
      holdStatusFilter = HoldStatus.None;
      dispatchableTypes = [DispatchableType.Dispatchable];
      break;
    case ShipmentsPrimaryFilterType.DELIVERED:
      isClosedFilter = false;
      statusListFilter = [ShipmentStatus.Completed];
      break;
    case ShipmentsPrimaryFilterType.CLOSED:
      isClosedFilter = true;
      statusListFilter = undefined;
      break;
    case ShipmentsPrimaryFilterType.CUSTOM:
      serviceTypeIds = selectedCustomFilter?.serviceTypeIds || undefined;
      // override endCustomerIdsFilter if selectedCustomFilter has endCustomerIds
      endCustomerIdsFilter = selectedCustomFilter?.endCustomerIds || undefined;
      excludeWordsFromNoteFilter =
        selectedCustomFilter?.excludeWordsFromNote || undefined;
      statusListFilter = selectedCustomFilter?.shipmentStatuses || undefined;
      // Combine (AND) the custom filter's own driver roster with whatever
      // driver the dispatcher picked in the separate driver dropdown, rather
      // than letting the custom filter silently override that pick.
      driverIdsFilter =
        driverIdsFilter && selectedCustomFilter?.driverIds
          ? driverIdsFilter.filter((id) =>
              selectedCustomFilter!.driverIds!.includes(id)
            )
          : driverIdsFilter || selectedCustomFilter?.driverIds || undefined;
      isConfirmed = selectedCustomFilter?.isConfirmed ?? undefined;
      isAttempted = selectedCustomFilter?.isAttempted ?? undefined;
      recurrenceTypes = selectedCustomFilter?.recurrenceTypes || undefined;
      break;
  }

  const effectiveMaxShipmentDate = shipmentDateRangeEnd ?? maxDate;

  return {
    courierId: courierId,
    lateByFilter: lateByFilter,
    minClosingTimeFilter: minClosingTimeFilter,
    likelyLateOnlyFilter: likelyLateOnlyFilter,
    minExpectedCompletionDate: minDate
      ? datePickerDateToISODate(minDate)
      : undefined,
    minShipmentDate: shipmentDateRangeStart
      ? datePickerDateToISODate(shipmentDateRangeStart)
      : undefined,
    maxShipmentDate: effectiveMaxShipmentDate
      ? datePickerDateToISODate(effectiveMaxShipmentDate)
      : undefined,
    minCompletedDate: completedDateRangeStart
      ? datePickerDateToISODate(completedDateRangeStart)
      : undefined,
    maxCompletedDate: completedDateRangeEnd
      ? datePickerDateToISODate(completedDateRangeEnd)
      : undefined,
    isClosedFilter: isClosedFilter,
    statusListFilter: statusListFilter,
    driverIdsFilter: driverIdsFilter,
    isAssignedFilter: isAssignedFilter,
    dispatchableTypes: dispatchableTypes,
    holdStatusFilter: holdStatusFilter,
    endCustomerIdsFilter: endCustomerIdsFilter,
    serviceTypeIds: serviceTypeIds,
    excludeWordsFromNote: excludeWordsFromNoteFilter,
    isConfirmed: isConfirmed,
    isAttempted: isAttempted,
    recurrenceTypes: recurrenceTypes,
  };
}
